import React from 'react';
import {createSelector} from 'reselect';
import type {
  OpenCanvasFeatureConfig,
  OpenCanvasStyleSheetOptions,
  Viewport,
} from '@packages/systems/open-canvas/core';
import {WF_FRAME_ID_ATTRIBUTE} from '@packages/systems/open-canvas/ui';
import {selectComponents} from '@packages/utilities/platform-selectors';
import {
  getFrameIdAtPath,
  selectIsComponentCanvasActive,
  selectSelectedFrameElementId,
  type ComponentCanvasStateSlice,
} from '../flux/selectors';
import {componentCanvasNodeClickedInInactiveFrame} from '../flux/actions';

/**
 * The default viewport position for the Component Canvas on the X axis.
 */
const DEFAULT_INITIAL_OFFSET_X = -50;

/**
 * The default viewport position for the Component Canvas on the Y axis.
 * This value takes into account the height of the Frame's toolbar
 */
const DEFAULT_INITIAL_OFFSET_Y = -90;

/**
 * Viewport scale used when a Component Canvas component has 2+ multi-state
 * preview scenarios, so sibling state frames are visible without manual zoom.
 * Mirrors MCC's lower default when showing all breakpoints.
 */
export const COMPONENT_CANVAS_MULTI_STATE_VIEWPORT_SCALE = 0.5;

/**
 * Minimum number of preview scenarios that triggers the multi-state default
 * viewport scale.
 */
export const COMPONENT_CANVAS_MULTI_STATE_SCENARIO_THRESHOLD = 2;

/**
 * Returns the Component Canvas default viewport scale for a given preview
 * scenario count (50% when there are 2+ states, otherwise 100%).
 */
export function getComponentCanvasViewportScale(scenarioCount: number): number {
  return scenarioCount >= COMPONENT_CANVAS_MULTI_STATE_SCENARIO_THRESHOLD
    ? COMPONENT_CANVAS_MULTI_STATE_VIEWPORT_SCALE
    : 1;
}

/**
 * Builds the Component Canvas default viewport for the given scale.
 * Screen-space offsets are divided by scale (same pattern as MCC) so the
 * frame toolbar clearance stays consistent at non-100% zoom.
 */
export function getComponentCanvasDefaultViewportPosition(
  scale: number = 1
): Viewport {
  return {
    x: DEFAULT_INITIAL_OFFSET_X / scale,
    y: DEFAULT_INITIAL_OFFSET_Y / scale,
    k: scale,
  };
}

/**
 * The default viewport position for the Component Canvas.
 */
export const DEFAULT_COMPONENT_CANVAS_VIEWPORT_POSITION: Viewport =
  getComponentCanvasDefaultViewportPosition(1);

/**
 * Returns the id of the current active Frame on the Component Canvas.
 * This represents the current Frame the user is interacting within, and is expected to be set when:
 * 1. An element inside the frame is selected, exposing settings and controls for the selected element data
 * 2. The frame itself is selected, exposing settings and controls for the Frame data
 */
const selectComponentCanvasActiveFrameId = createSelector(
  selectComponents,
  (state: ComponentCanvasStateSlice) =>
    state.UiNodeStore.previewModePreservedSelection
      ?.focusedSymbolNativeIdPath ||
    state.UiNodeStore.focusedSymbolNativeIdPath,
  getFrameIdAtPath
);

const selectComponentCanvasRenderedFrameIds = createSelector(
  selectComponentCanvasActiveFrameId,
  (activeFrameId): readonly string[] => (activeFrameId ? [activeFrameId] : [])
);

const COMPONENT_CANVAS_STYLE_SHEET_OPTIONS: OpenCanvasStyleSheetOptions = {
  filterOpenCanvasBodyStyles: true,
  bodyElementStyleTarget: `div:where([${WF_FRAME_ID_ATTRIBUTE}])`,
};

/**
 * Configuration for the Component Canvas feature to be used with the open canvas system
 */
export const componentCanvasOpenCanvasConfig: OpenCanvasFeatureConfig<ComponentCanvasStateSlice> =
  {
    isActive: selectIsComponentCanvasActive,
    selectors: {
      selectActiveFrameId: selectComponentCanvasActiveFrameId,
      selectRenderedFrameIds: selectComponentCanvasRenderedFrameIds,
      selectSelectedFrameId: selectSelectedFrameElementId,
      selectStyleSheetOptions: () => COMPONENT_CANVAS_STYLE_SHEET_OPTIONS,
    },
    actions: {
      inactiveFrameNodeClicked: componentCanvasNodeClickedInInactiveFrame,
    },
    ui: {
      renderFrames: () => <InternalComponentCanvasFramesRenderer />,
    },
    viewport: {
      getDefaultPosition: (
        _frameWidth?,
        _viewportWidth?,
        _frameOffsetX?,
        scale = 1
      ) => getComponentCanvasDefaultViewportPosition(scale),
    },
  };

// ----
// Below we are bridging the Component Canvas Frames renderer component from `/designer-flux` to the Open Canvas system.
// ----

let InternalComponentCanvasFramesRendererComponent: React.ComponentType | null =
  null;

const InternalComponentCanvasFramesRenderer = () => {
  return InternalComponentCanvasFramesRendererComponent ? (
    <InternalComponentCanvasFramesRendererComponent />
  ) : null;
};

/**
 * Bridge the Component Canvas Frames renderer to the Open Canvas system.
 *
 * This function needs to be called with `ComponentCanvasFramesRenderer` because the component
 * is defined in `/designer-flux` and cannot be imported by its configuration.
 */
export const bridgeComponentCanvasFramesRenderer = (
  Component: React.ComponentType
) => {
  if (InternalComponentCanvasFramesRendererComponent !== null) {
    throw new Error(
      'ComponentCanvasFramesRenderer is already set. Only one bridge per feature is supported.'
    );
  }

  InternalComponentCanvasFramesRendererComponent = Component;
};
